// Utility to intelligently truncate a testimonial at a natural sentence break.
// Preserves the reviewer's voice — never rewrites, only clips.
//
// Returns { preview, isTruncated, endsAtSentence } where:
//   • preview is the shortened text.
//   • isTruncated indicates whether the full text is longer than the preview.
//   • endsAtSentence signals whether the caller should skip the trailing "…"
//     (already ends at a natural full-stop, so the ellipsis would be redundant).
export function truncateAtSentence(text, min = 180, max = 220) {
  if (!text || text.length <= max) {
    return { preview: text, isTruncated: false, endsAtSentence: true };
  }

  // Window we search in for a natural break: [min, max]
  const window = text.slice(0, max);

  // Prefer end-of-sentence punctuation (. ! ?) after `min`
  const strong = window.slice(min).search(/[.!?]\s/);
  if (strong !== -1) {
    const cut = min + strong + 1; // include the punctuation
    return {
      preview: text.slice(0, cut).trim(),
      isTruncated: true,
      endsAtSentence: true,
    };
  }

  // Otherwise prefer a soft punctuation (— , ;) after `min`
  const soft = window.slice(min).search(/[,;—–]\s/);
  if (soft !== -1) {
    const cut = min + soft; // exclude the comma so the ellipsis reads naturally
    return {
      preview: text.slice(0, cut).trim(),
      isTruncated: true,
      endsAtSentence: false,
    };
  }

  // Fallback: clip at the last space before `max` to avoid cutting mid-word.
  const lastSpace = window.lastIndexOf(" ");
  const cut = lastSpace > min ? lastSpace : max;
  return {
    preview: text.slice(0, cut).trim(),
    isTruncated: true,
    endsAtSentence: false,
  };
}
